import { useState } from 'react';
import { DASH, formatInt } from './marketingMath';

export interface BreakdownRow { key: string; label: string; spend: number | null; clicks: number | null; results: number | null }
type Metric = 'spend' | 'clicks' | 'results';
const METRICS: [Metric, string][] = [['spend', 'Spend'], ['clicks', 'Clicks'], ['results', 'Results']];
const aed = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (m: Metric, v: number | null) => (v == null ? DASH : m === 'spend' ? `AED ${aed.format(v)}` : formatInt(v));

interface Props {
  title: string;
  rows: BreakdownRow[];
  /** Keep the server order (hour of day) instead of sorting by the metric. */
  keepOrder?: boolean;
  limit?: number;
  testId: string;
}

/** Spec 084 Ads tab — one Meta breakdown as a small labelled bar list (hand SVG) with a hidden table. */
export default function BreakdownCard({ title, rows, keepOrder, limit = 12, testId }: Props) {
  const [metric, setMetric] = useState<Metric>('spend');
  const shown = (keepOrder ? rows : [...rows].sort((a, b) => (b[metric] ?? -1) - (a[metric] ?? -1))).slice(0, limit);
  const max = Math.max(1, ...shown.map((r) => r[metric] ?? 0));
  const rowH = 22;
  const W = 360; const labelW = 96; const valueW = 92; const barMax = W - labelW - valueW;
  return (
    <section className="mkt-card mkt-breakdown" data-testid={testId}>
      <div className="mkt-card__head">
        <h4 className="mkt-card__title">{title}</h4>
        <div className="dash-toggle" role="group" aria-label={`${title}: measure`}>
          {METRICS.map(([m, label]) => (
            <button key={m} type="button" className={`dash-toggle__btn${metric === m ? ' dash-toggle__btn--active' : ''}`}
              aria-pressed={metric === m} onClick={() => setMetric(m)}>{label}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? <p className="mkt-empty__range">No breakdown for this range</p> : (
        <svg viewBox={`0 0 ${W} ${shown.length * rowH}`} role="img" aria-label={`${title} by ${metric}`} className="mkt-breakdown__svg">
          {shown.map((r, i) => {
            const v = r[metric];
            const y = i * rowH;
            return (
              <g key={r.key}>
                <title>{`${r.label}: ${fmt(metric, v)}`}</title>
                <text className="mkt-funnel__split" x={0} y={y + 15}>{r.label}</text>
                <rect className="mkt-funnel__track" x={labelW} y={y + 4} width={barMax} height={rowH - 8} rx={3} />
                {v != null && v > 0 && <rect className="mkt-funnel__bar mkt-funnel__bar--a" x={labelW} y={y + 4} width={Math.max(2, (v / max) * barMax)} height={rowH - 8} rx={3} />}
                <text className="mkt-funnel__split mkt-num-text" x={W} y={y + 15} textAnchor="end">{fmt(metric, v)}</text>
              </g>
            );
          })}
        </svg>
      )}
      <table className="sr-only">
        <caption>{title}</caption>
        <thead><tr><th scope="col">Group</th><th scope="col">Spend</th><th scope="col">Clicks</th><th scope="col">Results</th></tr></thead>
        <tbody>
          {rows.map((r) => <tr key={r.key}><th scope="row">{r.label}</th><td>{fmt('spend', r.spend)}</td><td>{fmt('clicks', r.clicks)}</td><td>{fmt('results', r.results)}</td></tr>)}
        </tbody>
      </table>
    </section>
  );
}
